import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Loader2 } from 'lucide-react';
import TypeEntrada from '@/svg-icons/type-guiada';
import ResendEnvelope from '@/svg-icons/resend-envelope';
import ResyncEnvelope from '@/svg-icons/resync-envelope';
import PropTypes from 'prop-types';

import Modal from '@/components/common/Modal';
import { orderType as parseOrderType } from '@/constants';
import ApiContext from '@/contexts/ApiContext';
import { handleAIVerification, isAIVerificationAvailable } from '@/helpers/aiVerification';
import ModalAIVerification from '@/orderDetails/ModalAIVerification/ModalAIVerification';
import { Button } from '@/components/ui/button';
import { showToast } from '@/store/ducks/toasts';

const inspectionStateMachine = {
  active: {
    WAITING: ['REJECT', 'ACCEPT'],
    ACCEPTED: ['ASSIGN_INSPECTOR'],
    ASSIGNED: [],
    IN_PROGRESS: [],
    FINALIZED_BY_DEVICE: ['REVIEW', 'SEND'],
    VNR: ['CANCEL'],
  },
  rejected: {
    REJECTED: ['ARCHIVE'],
  },
  sent: {
    FINISHED: ['ARCHIVE'],
  },
  independent: {
    PENDING: ['ASSIGN_ORDER'],
    IN_PROGRESS: ['ASSIGN_ORDER'],
    FINALIZED: ['ASSIGN_ORDER', 'MERGE_ORDER'],
  },
  archived: {
    ACCEPTED: [],
    ASSIGNED: [],
    IN_PROGRESS: [],
    FINALIZED_BY_DEVICE: [],
    FINISHED: [],
    REJECTED: [],
  },
  client: {
    WAITING: [],
    ACCEPTED: [],
    ASSIGNED: [],
    IN_PROGRESS: [],
    FINALIZED_BY_DEVICE: [],
    FINISHED: [],
    REJECTED: [],
  },
  preview: {
    PENDING: ['BACK_TO_ORDER'],
    IN_PROGRESS: ['BACK_TO_ORDER'],
    FINALIZED: ['BACK_TO_ORDER'],
  },
  canceled: {
    VNR: [],
  },
};

const marketPlaceStateMachine = {
  active: {
    WAITING: ['REJECT', 'ACCEPT'],
    ACCEPTED: ['DOCUSIGN_ACCEPTED'],
    ASSIGNED: [],
    IN_PROGRESS: [],
    FINALIZED_BY_DEVICE: ['SEND'],
  },
  rejected: {
    REJECTED: ['ARCHIVE'],
  },
  sent: {
    FINISHED: ['ARCHIVE'],
  },
  archived: {
    ACCEPTED: [],
    ASSIGNED: [],
    IN_PROGRESS: [],
    FINALIZED_BY_DEVICE: [],
    FINISHED: [],
    REJECTED: [],
  },
  client: {
    WAITING: [],
    ACCEPTED: ['DOCUSIGN_ACCEPTED'],
    ASSIGNED: [],
    IN_PROGRESS: [],
    FINALIZED_BY_DEVICE: ['SEND'],
    FINISHED: [],
    REJECTED: [],
  },
  preview: {
    PENDING: ['BACK_TO_ORDER'],
    IN_PROGRESS: ['BACK_TO_ORDER'],
    FINALIZED: ['BACK_TO_ORDER'],
  },
};

const OrderDetailsFooter = ({
  backToOrder,
  changePage,
  changeOrderStatus,
  children,
  currentPage,
  data,
  marketPlace,
  newStatus,
  openAssignDevice,
  openAssignOrder,
  openMergeOrder,
  orderEdit,
  orderStatus,
  resendEnvelope,
  resyncEnvelope,
  resendSuccess,
  resyncSuccess,
  readonly,
  address,
  medias,
  requestInProgress,
  saveMedia,
  configureBryEnvelope,
  bryConfigLoading,
  clientPanel,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  
  const me = JSON.parse(window.sessionStorage.getItem('me') || '{}');
  const features = (me.features || []).map(feature => parseOrderType.get(feature));
  const permissions = JSON.parse(window.sessionStorage.getItem('orderTypeConfigurations') || '{}');

  const officialInspection = data.inspections?.find(i => i.official === true);

  const [showAIModal, setShowAIModal] = useState(false);
  const [aiValidationResults, setAiValidationResults] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const handleAIVerificationClick = () => {
    if (!officialInspection) return;

    const onSuccess = validationResults => {
      setAiValidationResults(validationResults);
      setShowAIModal(true);
      setAiLoading(false);
    };

    const onError = error => {
      console.error(error);
      setAiLoading(false);
      dispatch(showToast({
        type: 'error',
        title: intl.formatMessage({ id: 'ERROR' }),
        text: intl.formatMessage({ id: 'UNKNOWN_ERROR' }),
        duration: 5000,
      }));
    };

    handleAIVerification(officialInspection.id, onSuccess, onError, setAiLoading);
  };

  const closeAIModal = () => {
    setShowAIModal(false);
    setAiValidationResults(null);
  };

  const isSupportModeActive = () => {
    return document.cookie.includes('SupportMode=true');
  };

  const shouldShowExtraActions = () => {
    if (
      !permissions ||
      !permissions[data.type] ||
      !permissions[data.type][data.status] ||
      !permissions[data.type][data.status].extraActions
    ) {
      return false;
    }

    return permissions[data.type][data.status].extraActions.some(extraAction => features.indexOf(extraAction) > -1);
  };

  const shouldShowButton = (feature) => {
    return (
      permissions &&
      permissions[data.type] &&
      permissions[data.type][data.status] &&
      permissions[data.type][data.status].extraActions &&
      permissions[data.type][data.status].extraActions.indexOf(feature) > -1 &&
      features.indexOf(feature) > -1
    );
  };

  const handleChangePage = (event, targetPage) => {
    if (typeof changePage !== 'function') return;

    if (clientPanel) {
      const clientActionMap = {
        'extra-actions': 'extra',
        checklist: 'checklist-form',
        openCancelOrder: 'openCancelOrder',
      };

      const action = clientActionMap[targetPage] || targetPage;
      const params = data?.id ? { orderId: data.id } : null;
      changePage(params, action);
      return;
    }

    const panelActionMap = {
      extra: 'extra-actions',
      'checklist-form': 'checklist',
      openCancelOrder: 'openCancelOrder',
    };

    changePage(panelActionMap[targetPage] || targetPage);
  };

  const renderButton = (onClick, textKey, btnType = "primary", processing = false, disabled = false) => {
    let bgColor = "bg-[#859905]"; // Primary Green
    if (btnType === "destructive") bgColor = "bg-[#be1104]"; // Secondary Red
    if (btnType === "tertiary") bgColor = "bg-[#1C3B5E]"; // Tertiary Navy
    if (btnType === "review") bgColor = "bg-[#808080]"; // Review / gray button

    return (
      <span className="relative inline-block my-[30px] mx-[5px]" key={textKey}>
        <Button
          variant="default"
          onClick={onClick}
          disabled={processing || disabled}
          className={`w-[150px] h-[34px] rounded-[2px] text-white btn-roboto normal-case border-none shadow-none text-[13px] leading-none transition-all duration-200 hover:opacity-[0.75] ${bgColor}`}
          data-cy={`order-details-footer-button-${textKey}`}
        >
          <span className={processing ? "invisible" : ""}>
            <FormattedMessage id={`BUTTON.${textKey}`} />
          </span>
        </Button>
        {processing && (
          <Loader2 className="absolute inset-0 m-auto h-5 w-5 animate-spin text-white" />
        )}
      </span>
    );
  };

  const renderNewButton = ({
    onClick,
    textKey,
    labelId = null,
    icon = null,
    isResync = false,
    isExtraActions = false,
    isSuccess = false,
    loading = false,
    disabled = false
  }) => {
    const widthClass = isResync ? "w-[144px]" : "w-[196px]";
    
    const baseStyles = isSuccess
      ? "border-[#ececec] text-[#c5c5c5] bg-[#ececec] cursor-not-allowed"
      : "border-[#e3e3e3] text-[#393939] bg-white hover:opacity-75 disabled:opacity-75 disabled:cursor-not-allowed";

    const extraActionsStyles = isExtraActions ? 'bg-[#e3e3e3] border-[#e3e3e3]' : '';

    return (
      <span className="inline-block my-[30px] mx-[5px]">
        <Button
          variant="ghost"
          onClick={onClick}
          disabled={disabled || loading || isSuccess}
          className={`relative inline-flex items-center justify-center h-[36px] p-0 border rounded-[3px] font-['Open_Sans',_sans-serif] text-[14px] font-bold shadow-none transition-all duration-200 ${widthClass} ${baseStyles} ${extraActionsStyles}`}
          data-cy={`order-details-footer-button-${textKey}`}
        >
          {loading ? (
            <Loader2 className="absolute inset-0 m-auto h-5 w-5 animate-spin text-gray-500" />
          ) : isSuccess ? (
            <span>
              {textKey === 'RESEND_ENVELOPE' ? 'Envelope enviado' : 'Sincronizado'}
            </span>
          ) : (
            <span className="flex items-center justify-center">
              {icon && <span className="mr-[5px] flex items-center justify-center [&_svg]:w-[14px] [&_svg]:h-[14px] [&_svg_path]:fill-[#393939]">{icon}</span>}
              <FormattedMessage id={labelId || `BUTTON.${textKey}`} />
            </span>
          )}
        </Button>
      </span>
    );
  };

  let actionButtons = [];
  if (readonly === true) {
    actionButtons = null;
  } else if (newStatus !== null && !data.type.startsWith('marketplace_')) {
    let disabled = false;
    let action = changeOrderStatus;

    if (newStatus === 'APPROVE') {
      const { address_validation: addressValidation } = data;
      if (
        me?.features?.includes('SCHEDULE') &&
        (addressValidation?.state === 'invalid' ||
          addressValidation?.city === 'invalid' ||
          addressValidation?.neighborhood === 'invalid') &&
        !address
      ) {
        disabled = true;
      }
    }

    if (
      newStatus === 'SEND' &&
      data?.type === 'tourVirtual' &&
      (medias.length < 1 || medias[0].isValid !== undefined)
    ) {
      disabled = medias.filter(media => media.type === 'TOUR_VIRTUAL' && media.isValid === true).length < 1;
      action = async status => {
        try { await saveMedia(); } catch (err) { console.log(err); }
        changeOrderStatus(status);
      };
    }

    actionButtons = [
      (p) => renderButton(() => changeOrderStatus(null), 'CANCEL', "destructive", p),
      (p) => renderButton(() => action(newStatus), 'CONFIRM', "primary", p, disabled),
    ];
  } else if (data.type === 'marketplace_docusign' && data.status === 'IN_PROGRESS') {
    const buttonVisible = permissions.docusign && permissions.docusign[data.status].canBeEdited;
    if (buttonVisible) {
      actionButtons.push((status) => 
        renderNewButton({
          onClick: evt => resendEnvelope(evt),
          textKey: 'RESEND_ENVELOPE',
          icon: <ResendEnvelope />,
          isSuccess: resendSuccess,
          loading: status === 'WORKING_RESEND',
        })
      );
      if (isSupportModeActive()) {
        actionButtons.push((status) => 
          renderNewButton({
            onClick: evt => resyncEnvelope(evt),
            textKey: 'RESYNC_ENVELOPE',
            icon: <ResyncEnvelope />,
            isResync: true,
            isSuccess: resyncSuccess,
            loading: status === 'WORKING_RESYNC',
          })
        );
      }
    }
  } else if (data.type === 'marketplace_bry' && ['ACCEPTED', 'IN_PROGRESS'].includes(data.status)) {
    const buttonVisible = permissions.bry && permissions.bry[data.status].canBeEdited;
    if (buttonVisible) {
      actionButtons.push((status) => 
        renderNewButton({
          onClick: configureBryEnvelope,
          textKey: 'CONFIGURE_ENVELOPE',
          loading: bryConfigLoading,
          disabled: status === 'WORKING_RESEND',
        })
      );
    }
    if (isSupportModeActive()) {
      actionButtons.push((status) => 
        renderNewButton({
          onClick: evt => resyncEnvelope(evt),
          textKey: 'RESYNC_ENVELOPE',
          icon: <ResyncEnvelope />,
          isResync: true,
          isSuccess: resyncSuccess,
          loading: status === 'WORKING_RESYNC',
        })
      );
    }
  } else if (shouldShowExtraActions()) {
    const isCurtaTemporadaChecklist = data.type === 'curtaTemporada' && shouldShowButton('checklist');
    const labelId = isCurtaTemporadaChecklist ? "ASSISTED_INSPECTION" : "BUTTON.EXTRA_ACTIONS";
    const pageTarget = isCurtaTemporadaChecklist ? 'checklist' : 'extra-actions';

    actionButtons.push((processing) => 
      renderNewButton({
        onClick: e => handleChangePage(e, pageTarget),
        textKey: labelId,
        labelId: labelId,
        icon: <TypeEntrada />,
        isExtraActions: true,
        disabled: processing,
      })
    );
  } else {
    const isMarketplace = data.type && data.type.includes('marketplace_');
    const buttonsStateMachine = isMarketplace ? marketPlaceStateMachine : inspectionStateMachine;
    if (buttonsStateMachine[currentPage] && buttonsStateMachine[currentPage][orderStatus]) {
      actionButtons = buttonsStateMachine[currentPage][orderStatus].map((buttonType) => {
        switch (buttonType) {
          case 'ACCEPT': return (p) => renderButton(() => changeOrderStatus('APPROVE'), 'ACCEPT', "primary", p);
          case 'REJECT': return (p) => renderButton(() => changeOrderStatus('REJECT'), 'REJECT', "destructive", p);
          case 'ASSIGN_INSPECTOR': return (p) => renderButton(() => openAssignDevice(), 'ASSIGN_INSPECTOR', "primary", p);
          case 'SEND': return (p) => renderButton(() => changeOrderStatus('SEND'), 'SEND', "primary", p);
          case 'REVIEW': {
            if (!officialInspection || !isAIVerificationAvailable(officialInspection?.type)) return null;
            const isDisabled = aiLoading;
            return (p) => renderButton(() => handleAIVerificationClick(), 'REVIEW', "review", p || aiLoading, isDisabled);
          }
          case 'ASSIGN_ORDER': return (p) => renderButton(() => openAssignOrder(data.form.inspection_type), 'ASSIGN_ORDER', "primary", p);
          case 'MERGE_ORDER': return (p) => renderButton(() => openMergeOrder(data.form.inspection_type), 'MERGE_ORDER', "tertiary", p);
          case 'EDIT': return (p) => renderButton(() => orderEdit(), 'EDIT', "primary", p);
          case 'DOCUSIGN_ACCEPTED': return (p) => renderButton(() => marketPlace('DOCUSIGN_ACCEPTED', data), 'SEND', "primary", p);
          case 'BACK_TO_ORDER': return (p) => renderButton(() => backToOrder(), 'BACK_TO_ORDER', "primary", p);
          case 'CANCEL': return (p) => renderButton(() => handleChangePage(null, 'openCancelOrder'), 'CANCEL_ORDER', "destructive", p);
          default: return null;
        }
      }).filter(Boolean);
    }
  }

  if (!actionButtons || (actionButtons.length === 0 && !children)) return null;

  return (
    <ApiContext.Consumer>
      {apiContext => (
        <div className="sticky bottom-[-20px] left-0 right-0 w-full text-center bg-white z-[3]">
          {actionButtons.length > 0 && <hr className="block border-t border-[#e9e9e9] m-0 p-0" />}
          {children}
          {data.type === 'marketplace_docusign' && data.status === 'IN_PROGRESS' ? (
            <div className="flex justify-center items-center">
              {actionButtons.map(f => (f ? f(apiContext.status) : null))}
            </div>
          ) : (
            actionButtons.map(f => {
              const isWorking = apiContext.status === 'WORKING' || requestInProgress;
              return f ? f(isWorking) : null;
            })
          )}
          
          {showAIModal && (
            <Modal onClose={closeAIModal}>
              <ModalAIVerification onClose={closeAIModal} validationResults={aiValidationResults} loading={aiLoading} />
            </Modal>
          )}
        </div>
      )}
    </ApiContext.Consumer>
  );
};

OrderDetailsFooter.propTypes = {
  data: PropTypes.object.isRequired,
  changeOrderStatus: PropTypes.func,
  openAssignDevice: PropTypes.func,
  openAssignOrder: PropTypes.func,
  openMergeOrder: PropTypes.func,
  orderStatus: PropTypes.string,
  orderEdit: PropTypes.func,
  resendEnvelope: PropTypes.func,
  resyncEnvelope: PropTypes.func,
  resendSuccess: PropTypes.bool,
  resyncSuccess: PropTypes.bool,
  configureBryEnvelope: PropTypes.func,
  bryConfigLoading: PropTypes.bool,
  newStatus: PropTypes.string,
  backToOrder: PropTypes.func,
  changePage: PropTypes.func,
  children: PropTypes.node,
  currentPage: PropTypes.string,
  marketPlace: PropTypes.func,
  readonly: PropTypes.bool,
  address: PropTypes.object,
  medias: PropTypes.array,
  requestInProgress: PropTypes.bool,
  saveMedia: PropTypes.func,
  clientPanel: PropTypes.bool,
};

OrderDetailsFooter.defaultProps = {
  newStatus: null,
  orderStatus: null,
  orderEdit: null,
  openAssignDevice: null,
  openAssignOrder: null,
  openMergeOrder: null,
  configureBryEnvelope: () => {},
  backToOrder: null,
  changePage: null,
  children: null,
  currentPage: null,
  marketPlace: null,
  readonly: null,
  address: null,
  medias: [],
  bryConfigLoading: false,
  resendEnvelope: () => {},
  resyncEnvelope: () => {},
  resendSuccess: false,
  resyncSuccess: false,
  requestInProgress: false,
  saveMedia: () => {},
  changeOrderStatus: () => {},
  clientPanel: false,
};

export default OrderDetailsFooter;
